import {
    useRef,
    type ComponentPropsWithRef,
    type HTMLAttributes,
    type KeyboardEvent,
    type MouseEvent,
    type ReactElement,
    type ReactNode,
    type RefObject,
} from 'react';
import { Link, type LinkProps } from 'react-router';
import Icon, { RemixIconNames, type IconNames } from '~/components/ui/Icon';
import Tooltip from '~/components/ui/Tooltip';
import '~/styles/components/new/button.scss';
import { classNames, type IconColor } from '~/utils/styles';
import Divider from './Divider';

// common props for Button and ButtonLink
type ButtonCommonProps = {
    action?: 'primary' | 'negative' | 'positive';
    disabled?: boolean; // included here bcs is not part of the html anchor props
    processing?: boolean;
    variant?: 'filled' | 'filled-light' | 'floating';
    icon?: IconNames;
    size?: 'medium' | 'large';
    'data-test-component'?: string;
    'data-test-name'?: string;
};

/**
 * BaseButton
 * This is a common component for Button and ButtonIcon, do not use it directly or export it.
 * It accepts a icon component instead of icon name for convenience to avoid passing variant and action just to get the icon color.
 */

type ButtonWithText = {
    children: ReactNode;
    iconComponent?: ReactElement<typeof Icon>;
    title?: string;
};

type IconOnlyButton = {
    children?: undefined | null; // includes null as part of the HTML button type;
    // required for icon-only
    iconComponent: ReactElement<typeof Icon>;
    title: string;
};

type ButtonIconRule = ButtonWithText | IconOnlyButton;

type BaseButtonProps = ButtonIconRule & ComponentPropsWithRef<'button'>;

const BaseButton = ({
    iconComponent,
    children,
    // html button props
    title,
    className = '',
    ...props
}: BaseButtonProps) => {
    return (
        <Tooltip title={title}>
            <button
                {...props}
                className={classNames({
                    button: true,
                    'button-rounded': children === undefined,
                    [className]: Boolean(className),
                })}>
                {iconComponent}
                {children}
            </button>
        </Tooltip>
    );
};

type iconColorActionVars = Extract<IconColor, `action-${string}`>;

const ICON_COLOR_BY_ACTION: Record<string, iconColorActionVars> = {
    primary: 'action-primary',
    negative: 'action-negative-dark',
    positive: 'action-positive-dark',
};

// helper that applies the rules for icon and icon color.
function getIconComponent(
    processing: boolean,
    variant: ButtonCommonProps['variant'],
    action: ButtonCommonProps['action'] = 'primary',
    icon?: IconNames,
) {
    const iconName = processing ? 'loading' : icon;

    // note that loading indicator shows even when no icon is provided
    return iconName ? (
        <Icon
            icon={iconName}
            size={16}
            colorVar={
                variant !== 'filled'
                    ? ICON_COLOR_BY_ACTION[action] // since floating and filled-light make clear background, paints icon as action color
                    : 'action-contrast' // otherwise, make it white
            }
        />
    ) : undefined;
}

/**
 * Button
 */

export type ButtonProps = ButtonCommonProps & Omit<BaseButtonProps, 'iconComponent'>;

export default function Button({
    variant = 'filled',
    action = 'primary',
    processing = false,
    size = 'large',
    icon,
    children,
    // html button props
    disabled = false,
    className = '',
    type = 'button', // default to button, for not submiting forms
    'data-test-component': testComponent = 'new/Button',
    'data-test-name': testName,
    // more html props
    ...props
}: ButtonProps) {
    return (
        <BaseButton
            {...props}
            iconComponent={getIconComponent(processing, variant, action, icon)}
            disabled={disabled || processing}
            className={classNames({
                [`size-${size} action-${action} variant-${variant}`]: true,
                [className]: Boolean(className),
            })}
            type={type}
            data-test-component={testComponent}
            data-test-name={testName}>
            {children}
        </BaseButton>
    );
}

/**
 * ButtonSplit
 */

export type ButtonSplitProps = ButtonCommonProps &
    Omit<BaseButtonProps, 'iconComponent' | 'variant'> & {
        onClickSplit?: (ev: MouseEvent<HTMLButtonElement>) => void;
        splitContent?: ReactNode;
    };

export function ButtonSplit({
    onClickSplit,
    // Button props
    action = 'primary',
    icon,
    processing = false,
    size = 'large',
    splitContent,
    children,
    // html button props
    disabled,
    className = '',
    onClick,
    // more html button props
    ...props
}: ButtonSplitProps) {
    const appletRef = useRef<HTMLElement>(null!);

    // only filled variant is supported for split button
    const variant = 'filled';

    return (
        <BaseButton
            {...props}
            disabled={disabled || processing}
            iconComponent={getIconComponent(processing, variant, action, icon)}
            onClick={(ev) => {
                const x = appletRef.current.getBoundingClientRect().x;
                if (ev.clientX > x) {
                    onClickSplit?.(ev);
                } else {
                    onClick?.(ev);
                }
            }}
            className={classNames({
                [`button-split size-${size} action-${action} variant-${variant}`]: true,
                [className]: Boolean(className),
            })}>
            <div className='split-content'>{children}</div>
            <SplitComponent ref={appletRef}>
                {splitContent ?? (
                    <Icon
                        icon={RemixIconNames.RiArrowDropDownLine}
                        size={16}
                    />
                )}
            </SplitComponent>
        </BaseButton>
    );
}

function SplitComponent({ ref, children }: { ref?: RefObject<HTMLElement>; children?: ReactNode }) {
    return (
        <>
            <Divider
                vertical
                className='button-divider'
                ref={ref}
            />
            {children}
        </>
    );
}

/**
 * ButtonLink
 */

// same rule as ButtonIconRule but with icon instead of iconComponent
type ButtonLinkWithTextIcon = Omit<ButtonWithText, 'iconComponent'> & { icon?: IconNames };
type IconOnlyButtonLinkIcon = Omit<IconOnlyButton, 'iconComponent'> & { icon: IconNames };

export type ButtonLinkIconRule = ButtonLinkWithTextIcon | IconOnlyButtonLinkIcon;

export type ButtonLinkProps = ButtonCommonProps &
    ButtonLinkIconRule &
    LinkProps &
    HTMLAttributes<HTMLAnchorElement>;

export function ButtonLink({
    action = 'primary',
    processing = false,
    size = 'large',
    icon,
    children,
    disabled,
    variant = 'filled',
    // html anchor props
    className = '',
    title = '',
    // other props (Link + anchor element)
    ...props
}: ButtonLinkProps) {
    const iconComp = getIconComponent(processing, variant, action, icon);

    // prevent default behaviour of link when disabled
    // css is not preventing keyboard events
    const disabledKeydown =
        disabled || processing
            ? (ev: KeyboardEvent<HTMLAnchorElement>) => {
                  ev.preventDefault();
              }
            : undefined;

    return (
        <Tooltip title={title}>
            <Link
                className={classNames({
                    [`button button-link action-${action} variant-${variant} size-${size}`]: true,
                    'button-rounded': children === undefined,
                    'no-underline': variant !== 'floating',
                    disabled: Boolean(disabled || processing),
                    [className]: Boolean(className),
                })}
                onKeyDown={disabledKeydown}
                {...props}>
                {iconComp}
                {children}
            </Link>
        </Tooltip>
    );
}

/**
 * PaginationButton
 */
export type PaginationButtonProps = Pick<
    ButtonProps,
    'onClick' | 'disabled' | 'className' | 'children'
> & {
    role?: 'number' | 'next' | 'prev' | 'more';
    selected?: boolean;
};

export function PaginationButton({
    onClick,
    disabled,
    className = '',
    children,
    role = 'number',
    selected = false,
}: PaginationButtonProps) {
    const icon = ICON_BY_ROLE[role];
    return (
        <Button
            icon={icon}
            disabled={disabled}
            variant={selected ? 'filled' : 'filled-light'}
            className={classNames({
                'button button-rounded button-pagination': true,
                selected,
                more: role === 'more',
                [className]: Boolean(className),
            })}
            onClick={selected || role === 'more' ? undefined : onClick}
            size='medium'
            data-test-component='new/PaginationButton'
            data-test-name={role}>
            {role === 'number' ? children : undefined}
        </Button>
    );
}
export const ICON_BY_ROLE: Record<string, IconNames> = {
    // no icon for role number
    next: RemixIconNames.RiArrowRightSLine,
    prev: RemixIconNames.RiArrowLeftSLine,
    more: RemixIconNames.RiMoreFill,
};

/**
 * ButtonGroup
 */

export type ButtonGroupAlignment = 'left' | 'right' | 'center' | 'sides';

const ButtonGroupAligns = {
    left: 'justify-start',
    right: 'justify-end',
    center: 'justify-center',
    sides: 'justify-between',
    modal: 'modal-footer',
};

export type ButtonGroupProps = {
    align?: ButtonGroupAlignment;
    noSpace?: boolean;
    children: ReactNode;
    className?: string;
};

export function ButtonGroup({ align = 'right', children, className = '' }: ButtonGroupProps) {
    const alignClass = ButtonGroupAligns[align] || 'justify-between';

    return (
        <div
            className={classNames({
                [`button-group ${alignClass}`]: true,
                [className]: Boolean(className),
            })}>
            {children}
        </div>
    );
}
